import React, { type ChangeEvent, type ReactNode, useState } from 'react';

import { AnimatePresence } from 'framer-motion';

import { ReactComponent as InfoIcon } from '@pkg/assets/icons/v2/info.svg';

import { Tooltip } from './components';

import {
    Content,
    ContentLabelWrapper,
    InfoText,
    Label,
    LabelIcon,
    LabelText,
    Loading,
    LoadingText,
    RadioElement,
    RadioVisual,
    Spinner,
    StyledInputRadio,
    TooltipIcon,
    TooltipInterior,
    TooltipWrapper,
} from './styles';

export interface InputRadioChangeResult {
    id: string;
    groupName: string;
    value: string;
    checked: boolean;
}

export interface InputRadioProps {
    id: InputRadioChangeResult['id'];
    groupName: InputRadioChangeResult['groupName'];
    value: InputRadioChangeResult['value'];
    label: string;
    icon?: ReactNode;
    infoText?: string;
    tooltip?: ReactNode;
    checked?: InputRadioChangeResult['checked'];
    disabled?: boolean;
    loading?: boolean;
    padded?: boolean;
    onChange(result: InputRadioChangeResult): void;
}

export function InputRadio({
    id,
    groupName,
    value,
    label,
    icon,
    infoText,
    tooltip,
    checked = false,
    disabled = false,
    loading = false,
    padded = false,
    onChange,
}: InputRadioProps) {
    const [showTooltip, setShowTooltip] = useState(false);

    function handleChange(event: ChangeEvent<HTMLInputElement>) {
        event.stopPropagation();
        onChange({ id, groupName, value, checked: event.target.checked });
    }

    const isDisabled = disabled || loading;

    const labelIconMarkup = icon ? <LabelIcon>{icon}</LabelIcon> : null;

    // TODO: Should also render on `:focus` of `label` or `input`.
    const tooltipIconMarkup = tooltip ? (
        <TooltipIcon
            $disabled={isDisabled}
            onMouseOver={isDisabled ? undefined : () => setShowTooltip(true)}
            onMouseLeave={() => setShowTooltip(false)}
        >
            <InfoIcon />
        </TooltipIcon>
    ) : null;

    // TODO: This is a terrible implementation and needs to be replaced.
    // Current solution was made to satisfy a tight deadline.
    const tooltipMarkup = tooltip ? (
        <TooltipWrapper key={`InputRadio-TooltipWrapper-${id}`}>
            <TooltipInterior key={`InputRadio-TooltipInterior-${id}`}>{tooltip}</TooltipInterior>
        </TooltipWrapper>
    ) : null;

    const infoTextMarkup = infoText ? <InfoText>{infoText}</InfoText> : null;

    // TODO: This content needs to be translated...
    // but not sure where to place "design system" translations.
    const loadingMarkup = loading ? (
        <Loading key={`InputRadio-Loading-${id}`}>
            <Spinner />
            <LoadingText>Loading this selection…</LoadingText>
        </Loading>
    ) : null;

    return (
        <StyledInputRadio>
            <Label htmlFor={id} $disabled={isDisabled} $loading={loading} $padded={padded}>
                <Content>
                    <ContentLabelWrapper>
                        {labelIconMarkup}
                        <LabelText>{label}</LabelText>
                        {tooltipIconMarkup}
                    </ContentLabelWrapper>

                    {infoTextMarkup}
                </Content>

                <RadioElement
                    id={id}
                    name={groupName}
                    value={value}
                    checked={checked}
                    disabled={isDisabled}
                    onChange={handleChange}
                />

                <RadioVisual $loading={loading}>
                    <AnimatePresence>{loadingMarkup}</AnimatePresence>
                </RadioVisual>
            </Label>

            <AnimatePresence>{showTooltip ? tooltipMarkup : null}</AnimatePresence>
        </StyledInputRadio>
    );
}

InputRadio.Tooltip = Tooltip;
